import config from 'config'
import { useSearchParams } from 'next/navigation'
import { FC, PropsWithChildren } from 'react'

import { FliptProvider, useFliptContext } from './flipt/flipt-provider'
import {
  LocalStorageFFProvider,
  useLocalStorageFF
} from './local-storage-feature-flags'
import { FeatureFlags } from './types'

type FeatureFlagsProviderProps = {
  ff: FeatureFlags
}

export const FeatureFlagsProvider: FC<
  PropsWithChildren<FeatureFlagsProviderProps>
> = ({ children, ff }) => {
  if (!config.NEXT_PUBLIC_FLIPT_TOKEN) {
    return <LocalStorageFFProvider>{children}</LocalStorageFFProvider>
  }

  return (
    <LocalStorageFFProvider>
      <FliptProvider ff={ff}>{children}</FliptProvider>
    </LocalStorageFFProvider>
  )
}

export const useFlags = () => {
  const search = useSearchParams()
  const { ff } = useLocalStorageFF()
  const fliptContext = useFliptContext()
  const flags = fliptContext?.flags || {}

  const queryFlagsOn = search?.has('flagsOn')
    ? (search?.get('flagsOn')?.split(',') as string[])
    : []
  const queryFlagsOff = search?.has('flagsOff')
    ? (search?.get('flagsOff')?.split(',') as string[])
    : []

  const featureFlags = Object.entries(flags).reduce(
    (acc, [key, flag]) => {
      acc[key] = flag.value

      return acc
    },
    {} as Record<string, boolean>
  )

  queryFlagsOn.forEach(flag => {
    featureFlags[flag] = true
  })

  queryFlagsOff.forEach(flag => {
    featureFlags[flag] = false
  })

  const { flags: localFlags } = ff || { flags: {} }

  Object.entries(localFlags).forEach(([key, value]) => {
    featureFlags[key] = value
  })

  return featureFlags
}
